const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

// Token management
let accessToken = localStorage.getItem('accessToken');
let refreshToken = localStorage.getItem('refreshToken');

function setTokens(access, refresh) {
    accessToken = access;
    refreshToken = refresh;
    if (access) {
        localStorage.setItem('accessToken', access);
    } else {
        localStorage.removeItem('accessToken');
    }
    if (refresh) {
        localStorage.setItem('refreshToken', refresh);
    } else {
        localStorage.removeItem('refreshToken');
    }
}

function clearTokens() {
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
}

// Fetch wrapper with auth
async function fetchWithAuth(url, options = {}) {
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(`${API_BASE}${url}`, {
        ...options,
        headers,
    });

    // Handle token expiration
    if (response.status === 401) {
        const data = await response.json();
        if (data.code === 'TOKEN_EXPIRED' && refreshToken) {
            // Try to refresh
            const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });

            if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                setTokens(refreshData.accessToken, refreshToken);
                headers['Authorization'] = `Bearer ${refreshData.accessToken}`;

                // Retry original request
                response = await fetch(`${API_BASE}${url}`, {
                    ...options,
                    headers,
                });
            } else {
                // Refresh failed, clear tokens
                clearTokens();
                window.dispatchEvent(new Event('auth:logout'));
            }
        }
    }

    return response;
}

// =============================================
// Auth API
// =============================================

export async function register(email, password, nickname) {
    const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, nickname }),
    });
    const data = await response.json();
    if (data.success) {
        setTokens(data.accessToken, data.refreshToken);
    }
    return data;
}

export async function login(email, password) {
    const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (data.success) {
        setTokens(data.accessToken, data.refreshToken);
    }
    return data;
}

export async function logout() {
    try {
        await fetchWithAuth('/auth/logout', { method: 'POST' });
    } finally {
        clearTokens();
    }
}

export async function getMe() {
    const response = await fetchWithAuth('/auth/me');
    return response.json();
}

export function isAuthenticated() {
    return !!accessToken;
}

// =============================================
// Gallery API
// =============================================

export async function getConfigurations({ tab = 'all', sort = 'hot', page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ tab, sort, page, limit });
    const response = await fetchWithAuth(`/configurations?${params}`);
    return response.json();
}

export async function getConfiguration(id) {
    const response = await fetchWithAuth(`/configurations/${id}`);
    return response.json();
}

export async function createConfiguration(data) {
    const response = await fetchWithAuth('/configurations', {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.json();
}

export async function updateConfiguration(id, data) {
    const response = await fetchWithAuth(`/configurations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
    return response.json();
}

export async function deleteConfiguration(id) {
    const response = await fetchWithAuth(`/configurations/${id}`, {
        method: 'DELETE',
    });
    return response.json();
}

export async function submitConfiguration(id) {
    const response = await fetchWithAuth(`/configurations/${id}/submit`, {
        method: 'POST',
    });
    return response.json();
}

export async function uploadConfigurationImages(id, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));

    // 每次从 localStorage 获取最新的 token
    const currentToken = localStorage.getItem('accessToken');

    const response = await fetch(`${API_BASE}/configurations/${id}/images`, {
        method: 'POST',
        headers: {
            ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}),
        },
        body: formData,
    });
    return response.json();
}

export async function getMyConfigurations({ status, page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);
    const response = await fetchWithAuth(`/configurations/my?${params}`);
    return response.json();
}

// =============================================
// Social API
// =============================================

export async function toggleLike(id) {
    const response = await fetchWithAuth(`/configurations/${id}/like`, {
        method: 'POST',
    });
    return response.json();
}

export async function toggleFavorite(id) {
    const response = await fetchWithAuth(`/configurations/${id}/favorite`, {
        method: 'POST',
    });
    return response.json();
}

export async function getComments(id, { page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    const response = await fetchWithAuth(`/configurations/${id}/comments?${params}`);
    return response.json();
}

export async function addComment(id, content) {
    const response = await fetchWithAuth(`/configurations/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content }),
    });
    return response.json();
}

export async function deleteComment(configId, commentId) {
    const response = await fetchWithAuth(`/configurations/${configId}/comments/${commentId}`, {
        method: 'DELETE',
    });
    return response.json();
}

export async function getFavorites({ page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    const response = await fetchWithAuth(`/users/favorites?${params}`);
    return response.json();
}

// Alias for profile page
export const getMyFavorites = getFavorites;

// =============================================
// Admin API
// =============================================

export async function getAdminStats() {
    const response = await fetchWithAuth('/admin/stats');
    return response.json();
}

export async function getPendingConfigurations({ status = 'pending', page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ status, page, limit });
    const response = await fetchWithAuth(`/admin/configurations?${params}`);
    return response.json();
}

export async function approveConfiguration(id) {
    const response = await fetchWithAuth(`/admin/configurations/${id}/approve`, {
        method: 'POST',
    });
    return response.json();
}

export async function rejectConfiguration(id, reason) {
    const response = await fetchWithAuth(`/admin/configurations/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
    return response.json();
}

export async function createOfficialConfiguration(data) {
    const response = await fetchWithAuth('/admin/configurations', {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return response.json();
}

export async function getAllAdminConfigurations({ status, is_official, search, page = 1, limit = 20 } = {}) {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);
    if (is_official !== undefined) params.append('is_official', is_official);
    if (search) params.append('search', search);
    const response = await fetchWithAuth(`/admin/configurations/all?${params}`);
    return response.json();
}

export async function getAdminConfigurationDetail(id) {
    const response = await fetchWithAuth(`/admin/configurations/${id}`);
    return response.json();
}

export async function updateAdminConfiguration(id, data) {
    const response = await fetchWithAuth(`/admin/configurations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
    return response.json();
}

export async function deleteAdminConfiguration(id) {
    const response = await fetchWithAuth(`/admin/configurations/${id}`, {
        method: 'DELETE',
    });
    return response.json();
}

export async function toggleOfficialStatus(id) {
    const response = await fetchWithAuth(`/admin/configurations/${id}/toggle-official`, {
        method: 'POST',
    });
    return response.json();
}

export async function setConfigCoverImage(id, mediaUrl) {
    const response = await fetchWithAuth(`/admin/configurations/${id}/cover`, {
        method: 'POST',
        body: JSON.stringify({ media_url: mediaUrl }),
    });
    return response.json();
}

export async function deleteConfigMedia(configId, mediaId) {
    const response = await fetchWithAuth(`/admin/configurations/${configId}/media/${mediaId}`, {
        method: 'DELETE',
    });
    return response.json();
}

export async function uploadAdminConfigurationImages(id, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));

    const response = await fetch(`${API_BASE}/admin/configurations/${id}/images`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
    });
    return response.json();
}

// =============================================
// Notifications API
// =============================================

export async function getNotifications({ page = 1, limit = 20, unreadOnly = false } = {}) {
    const params = new URLSearchParams({ page, limit });
    if (unreadOnly) params.append('unreadOnly', 'true');
    const response = await fetchWithAuth(`/notifications?${params}`);
    return response.json();
}

export async function getUnreadCount() {
    const response = await fetchWithAuth('/notifications/unread-count');
    return response.json();
}

export async function markAsRead(notificationId) {
    const response = await fetchWithAuth(`/notifications/${notificationId}/read`, {
        method: 'POST',
    });
    return response.json();
}

export async function markAllAsRead() {
    const response = await fetchWithAuth('/notifications/read-all', {
        method: 'POST',
    });
    return response.json();
}

export async function deleteNotification(notificationId) {
    const response = await fetchWithAuth(`/notifications/${notificationId}`, {
        method: 'DELETE',
    });
    return response.json();
}

// =============================================
// Video Upload API
// =============================================

export async function uploadConfigurationVideo(id, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('video', file);

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const progress = Math.round((e.loaded / e.total) * 100);
                onProgress(progress);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
            } else {
                reject(new Error('Upload failed'));
            }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('POST', `${API_BASE}/configurations/${id}/video`);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.send(formData);
    });
}

export async function deleteConfigurationVideo(id) {
    const response = await fetchWithAuth(`/configurations/${id}/video`, {
        method: 'DELETE',
    });
    return response.json();
}

// =============================================
// 3D Model Upload API
// =============================================

export async function upload3DModel(id, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('model3d', file);

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const progress = Math.round((e.loaded / e.total) * 100);
                onProgress(progress);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(JSON.parse(xhr.responseText));
            } else {
                try {
                    const error = JSON.parse(xhr.responseText);
                    resolve(error); // Return error object for handling
                } catch {
                    reject(new Error('Upload failed'));
                }
            }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('POST', `${API_BASE}/configurations/${id}/model3d`);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.send(formData);
    });
}

export async function delete3DModel(id) {
    const response = await fetchWithAuth(`/configurations/${id}/model3d`, {
        method: 'DELETE',
    });
    return response.json();
}

// =============================================
// Custom Texture Upload API
// =============================================

export async function uploadCustomTexture(file) {
    const formData = new FormData();
    formData.append('texture', file);

    const response = await fetch(`${API_BASE}/configurations/texture`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
    });
    return response.json();
}
